<template>
    <div v-if="results?.error === true">
        <h4>Oooops, an error occured.</h4>
    </div>
    <template v-else-if="!results">
        <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
    </template>
    <template v-else-if="filteredResults.length == 0">
        <h4>Keine Unternehmen gefunden.</h4>
    </template>

    <template v-else>
        <div class="specialistFilter--results">
            <Message v-if="selectionLimitReached" severity="warn" size="small" variant="simple">{{ selectionLimitMessage }}</Message>
            <div
                class="specialistFilter--results--item" 
                v-for="(specialist, idx) in visibleResults"
                :key="specialist.id"
                :data-distance="specialist.distance"
            >
                <Checkbox
                    :inputId="inputName + '-' + specialist.id"
                    :name="inputName + '[]'"
                    :value="specialist.id"
                    v-model="selectedSpecialistIds"
                    style="appearance: none; opacity: 0; width: 0; height: 0; display: block;"
                />
            
                <div class="result-item">
                    <div class="result-item--left">
                        <div class="left-top">
                            <div class="headline">{{ specialist.companyTitle }}</div>
                            <div class="distance"><Tag severity="success" :value="parseFloat(specialist.distance).toFixed(2).replace('.', ',') + ' km'"></Tag></div>
                        </div>
                        <div class="left-bottom">
                            <div class="locations">{{ specialist.address }}, {{ specialist.city }} {{ specialist.zip }}</div>
                            <div v-if="specialist.websiteUrl" class="contact-line">
                                <span class="contact-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                        <circle cx="12" cy="12" r="9"></circle>
                                        <path d="M3 12h18"></path>
                                        <path d="M12 3a15 15 0 0 1 0 18"></path>
                                        <path d="M12 3a15 15 0 0 0 0 18"></path>
                                    </svg>
                                </span>
                                <span>{{ specialist.websiteUrl }}</span>
                            </div>
                            <div v-if="specialist.telephone" class="contact-line">
                                <span class="contact-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M5 4h4l2 5-2.5 1.5a15 15 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"></path>
                                    </svg>
                                </span>
                                <span>{{ specialist.telephone }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="result-item--right">
                        <label :for="inputName + '-' + specialist.id" class="p-button p-component p-button-primary result-action">{{ isSelected(specialist.id) ? 'Ausgewählt' : 'Auswählen' }}</label>
                    </div>
                </div>
            </div>
            <div v-if="visibleResults.length < filteredResults.length" style="text-align:center; margin-top: 1rem;">
                <button type="button" class="p-button" @click="loadMore">Mehr laden</button>
            </div>
        </div>
    </template>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    data: Object,
    field: Object,
    fields: Object,
    step: Object,
    results: Object,
    formType: String
});

const emit = defineEmits(['update:autoheight']);
const inputName = computed(() => `${props.step.name}[_wd_search_box][wd_send_to_specialist]`);
const selectionLimitReached = ref(false);

const maxSelectionCount = computed(() => {
    return props.formType === 'application' ? 5 : 1;
});

const selectionLimitMessage = computed(() => {
    if (maxSelectionCount.value === 1) {
        return 'Es kann nur ein Fachbetrieb ausgewählt werden.';
    }

    return `Es können maximal ${maxSelectionCount.value} Fachbetriebe ausgewählt werden.`;
});

function normalizeSelection(value) {
    const values = Array.isArray(value) ? value : (value ? [value] : []);
    const selection = [];

    values.forEach((id) => {
        if (id === null || id === undefined || id === '') {
            return;
        }

        if (!selection.includes(id)) {
            selection.push(id);
        }
    });

    return selection;
}

const selectedSpecialistIds = computed({
    get() {
        const value = props.fields?.['_wd_search_box']?.['wd_send_to_specialist']?.value;
        return normalizeSelection(value);
    },
    set(value) {
        if (!props.fields?.['_wd_search_box']?.['wd_send_to_specialist']) {
            return;
        }

        const previousSelection = selectedSpecialistIds.value;
        let nextSelection = normalizeSelection(value);

        if (maxSelectionCount.value === 1 && nextSelection.length > 1) {
            const addedSpecialist = nextSelection.find((id) => !previousSelection.includes(id));
            nextSelection = [addedSpecialist ?? nextSelection[nextSelection.length - 1]];
        } else if (nextSelection.length > maxSelectionCount.value) {
            selectionLimitReached.value = true;
            return;
        }

        selectionLimitReached.value = false;
        props.fields['_wd_search_box']['wd_send_to_specialist'].value = nextSelection;
    }
});

const visibleCount = ref(10);
const activeSearchRadius = computed(() => {
    const filterValues = props.fields?._wd_filter?.value;
    if (!filterValues || filterValues.sf_select !== 'Postleitzahl') {
        return null;
    }

    const radius = Number(filterValues.sf_plzRangeValue);
    if (Number.isNaN(radius) || radius < 0) {
        return null;
    }

    return radius;
});

const filteredResults = computed(() => {
    if (!Array.isArray(props.results)) {
        return [];
    }

    const radius = activeSearchRadius.value;
    if (radius === null) {
        return props.results;
    }

    return props.results.filter((specialist) => {
        const distance = Number(specialist?.distance);

        if (Number.isNaN(distance) || distance < 0) {
            return true;
        }

        return distance <= radius;
    });
});

const visibleResults = computed(() => {
    return filteredResults.value.slice(0, visibleCount.value);
});

watch(
    () => [filteredResults.value.length, visibleCount.value],
    () => {
        emit('update:autoheight');
    }
);

async function loadMore() {
    visibleCount.value += 10;
    emit('update:autoheight');
}

function isSelected(id) {
    return selectedSpecialistIds.value.includes(id);
}
</script>
